import { Button, Select } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMemo, useState } from 'react';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { NewTestAccountForm } from './operation/authOp/NewTestAccountForm';
import { getLogInPrompt } from '~/common/test-account/get-login-prompt';
import { IconBrandGoogleFilled, IconMail } from '@tabler/icons-react';

export default function AuthStepEditDialog({
  selectedAccountId,
  handleAddStep,
  testContentId,
  stepId,
}: {
  selectedAccountId: string;
  handleAddStep?: (step: {
    description: string;
    testAccountId?: string;
    operations: {
      order: number;
      operation: {
        operationType: string;
        componentId: string;
        testAccount: { id: string };
      };
    }[];
  }) => Promise<void>;
  testContentId: string;
  handleEditOperation?: ({ accountId }: { accountId: string }) => Promise<void>;
  stepId?: string;
}) {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();
  const { data: accounts } = api.testAccount.list.useQuery({ projectId: projectId || '' });
  const [accountId, setAccountId] = useState<string>(selectedAccountId);
  const [isAddingNewAccount, setIsAddingNewAccount] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const { mutateAsync: updateStep } = api.testStep.update.useMutation({
    onError: (_) => {
      notifications.show({ message: 'Unable to update step', color: 'red' });
    },
    onSuccess: async () => {
      // TODO: The getTest is not refreshing. Needs to be fixed.
      await trpcUtils.testContent.get.invalidate({ id: testContentId });
      await trpcUtils.testContent.getCode.invalidate({ testContentId });
      await trpcUtils.testStep.getStepAccountDetails.invalidate({ stepId: stepId || '' });
    },
  });

  const selectedAccountDetails = useMemo(() => accounts?.find((a) => a.id === accountId), [accounts, accountId]);

  const { mutateAsync: deleteStepOperations } = api.testStep.deleteStepOperations.useMutation({
    onError: (_) => {
      notifications.show({ message: 'Unable to delete operations', color: 'red' });
    },
    onSuccess: async () => {
      // TODO:The getTest is not refreshing. Needs to be fixed.
      await trpcUtils.testContent.get.invalidate({ id: testContentId });
      await trpcUtils.testContent.getCode.invalidate({ testContentId });
    },
  });

  const handleSubmit = async ({ accountId }: { accountId: string }) => {
    const account = accounts?.find((a) => a.id === accountId);

    if (!account) {
      notifications.show({ message: 'No account found', color: 'red' });
      return;
    }

    if (stepId) {
      await updateStep({
        stepId,
        data: {
          description: getLogInPrompt(account),
          testAccountId: accountId,
        },
      });
      await deleteStepOperations({ stepId });
    }

    if (handleAddStep) {
      await handleAddStep({
        description: getLogInPrompt(account),
        operations: [],
        testAccountId: accountId,
      });
    }
  };

  const accountSelectData = useMemo(
    () => [
      { value: 'new', label: '+ Add New Account' },
      ...(accounts?.map((a) => ({ value: a.id, label: a.name })) || []),
    ],
    [accounts],
  );

  return (
    <div className="flex flex-col gap-6 px-2 py-4">
      <div>
        <h2 className="text-md font-semibold text-gray-700 dark:text-gray-300">{stepId ? 'Edit' : 'Add'} Login Step</h2>
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-sm font-medium">Test Account</div>
          {!isAddingNewAccount ?
            <div>
              <Select
                data={accountSelectData}
                value={accountId}
                onChange={(value) => {
                  if (value === 'new') {
                    setIsAddingNewAccount(true);
                  } else {
                    setAccountId(value || '');
                  }
                }}
                className="mt-1"
              />
              <div className="mt-8">
                {!!selectedAccountDetails && (
                  <div>
                    <div className="mb-2 text-sm font-medium opacity-50">Test Account Details</div>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{selectedAccountDetails.name}</div>
                      {selectedAccountDetails.type === 'GOOGLE' ?
                        <IconBrandGoogleFilled
                          size={16}
                          className="text-gray-500"
                        />
                      : <IconMail
                          size={16}
                          className="text-gray-500"
                        />
                      }
                    </div>
                    {!!selectedAccountDetails.description && (
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        {selectedAccountDetails.description}
                      </div>
                    )}
                    <div>{selectedAccountDetails.userName}</div>
                    <div className="italic">{selectedAccountDetails.password}</div>
                  </div>
                )}
              </div>
            </div>
          : <div className="mt-3 space-y-3">
              <NewTestAccountForm
                mutating={savingAccount}
                onMutate={() => setSavingAccount(true)}
                onSettled={() => setSavingAccount(false)}
                onSuccessSubmit={(accountId) => {
                  setAccountId(accountId);
                  setIsAddingNewAccount(false);
                }}
                onCancel={() => setIsAddingNewAccount(false)}
              />
            </div>
          }
        </div>
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        <Button
          variant="default"
          onClick={() => modals.closeAll()}
        >
          Cancel
        </Button>
        <Button
          disabled={!accountId || savingAccount || isAddingNewAccount}
          onClick={async () => {
            modals.closeAll();
            await handleSubmit({ accountId });
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
